function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import careersConfig from "./careers-config.json";
import { getPositionDetailsByID } from "/server/services/comeet-service/comeet-service";
import { getAshbyPositionDetailsByID } from "/server/services/ashby-service/ashby-service";
import { DEFAULT_UNLISTED_POSITION_DATA, UNLISTED_POSITION_ID } from "/constants/careers/careers";
import { isPositionRelevantToType } from "/server/services/comeet-service/position-type-service";
import * as Sentry from "@sentry/nextjs";
const LOCATIONS = {
    london: [
        "london"
    ],
    miami: [
        "miami"
    ],
    newyork: [
        "york"
    ],
    sf: [
        "sf",
        "san franc"
    ],
    sydney: [
        "sydney"
    ],
    telaviv: [
        "aviv"
    ],
    tokyo: [
        "tokyo",
        "japan"
    ],
    chicago: [
        "chicago"
    ],
    saopaulo: [
        "sao paulo"
    ],
    denver: [
        "denver"
    ],
    houston: [
        "houston"
    ],
    austin: [
        "austin"
    ],
    warsaw: [
        "warsaw",
        "warsha"
    ],
    singapore: [
        "singapore"
    ],
    melbourne: [
        "melbourne"
    ],
    paris: [
        "paris"
    ],
    munich: [
        "munich",
        "munchen"
    ],
    atlanta: [
        "atlanta"
    ],
    "remote-us": [
        "remoteus",
        "remote, us",
        "remote-us",
        "remote_us",
        "remote us",
        "us remote"
    ],
    "remote-latam": [
        "remotelatam",
        "remote, latam",
        "remote-latam",
        "remote_latam",
        "remote latam",
        "latam remote",
        "santiago remote",
        "buenos aires remote",
        "argentina remote"
    ],
    // remote-emea is checked before zurich/amsterdam so "Zurich Remote" / "Amsterdam Remote"
    // still bucket into the EMEA remote location rather than the in-office city entry below.
    "remote-emea": [
        "zurich remote",
        "amsterdam remote",
        "remote-emea"
    ],
    mexicocity: [
        "mexico city"
    ],
    zurich: [
        "zurich"
    ],
    amsterdam: [
        "amsterdam"
    ],
    brazil: [
        "brazil",
        "brasil"
    ]
};
const DEPARTMENT_ALIASES = {
    events: "customersucc"
};
// Substring patterns are matched on the LOWERCASED, trimmed department name (see getDeptID).
// Patterns mix Comeet vocabulary ("delivery & solutions", "empowerment & loyalty"...) and
// Ashby vocabulary ("lifecycle & monetization", "shared services"...). When adding a
// pattern, prefer the full distinctive phrase (no bare prefixes/suffixes) so unrelated
// teamNames or sub-org names can't trigger a false positive.
const DEPARTMENTS = {
    customersucc: [
        "client relations",
        "customer succ",
        "customer management",
        "empowerment & loyalty",
        "voice of costumer"
    ],
    marketing: [
        "marketing",
        "lifecycle & monetization"
    ],
    hr: [
        "hr",
        "people"
    ],
    rnd: [
        "r&d"
    ],
    design: [
        "design"
    ],
    product: [
        "product"
    ],
    finance: [
        "finance"
    ],
    consulting: [
        "consulting",
        "revenue growth"
    ],
    partnership: [
        "partnership"
    ],
    businessoperations: [
        "business operations"
    ],
    operations: [
        "operation"
    ],
    sfs: [
        "sfs"
    ],
    communications: [
        "communications"
    ],
    legal: [
        "legal"
    ],
    mondayFoundation: [
        "socialimpact",
        "monday.com foundation"
    ],
    payments: [
        "payments"
    ],
    gtm: [
        "gtm"
    ],
    customertechandai: [
        "tech & ai",
        "customer tech & ai"
    ],
    strategyandoperations: [
        "cco strategy & operations"
    ],
    customerexperience: [
        "support"
    ],
    services: [
        "delivery & solutions"
    ],
    informationtechnologyanddata: [
        "information technology",
        "business verticals",
        "shared services"
    ]
};
// Ashby occasionally returns names with trailing whitespace (e.g. "CEO Office "). Trim so
// the exact-name lookup against careers-config display names is not whitespace-sensitive.
const getDeptIdFromName = (deptName)=>{
    const trimmed = (deptName || "").trim();
    if (!trimmed) return undefined;
    const deptInfo = careersConfig.departments.find((d)=>d.name === trimmed);
    return deptInfo === null || deptInfo === void 0 ? void 0 : deptInfo.id;
};
const getDeptID = (departmentName)=>{
    const configMatch = getDeptIdFromName(departmentName);
    if (configMatch) return configMatch;
    if (!departmentName) return null;
    const normalizedName = departmentName.toLowerCase().trim();
    if (normalizedName.includes("unlisted")) return null;
    if (DEPARTMENT_ALIASES[normalizedName]) return DEPARTMENT_ALIASES[normalizedName];
    for (const [deptId, patterns] of Object.entries(DEPARTMENTS)){
        if (patterns.some((pattern)=>normalizedName.includes(pattern))) {
            return deptId;
        }
    }
    Sentry.captureException(new Error("Unrecognized department in position data"), {
        level: "error",
        extra: {
            departmentName,
            normalizedName,
            availableDepartmentIds: Object.keys(DEPARTMENTS)
        }
    });
    return null;
};
//HELPER METHOD
const getDeptIDFromPosition = (posObj)=>{
    //returns dept ID from a Comeet position obj
    const deptID = getDeptID(posObj.department);
    return deptID;
};
//HELPER METHOD
const getDeptInfo = (posObj)=>{
    const deptID = getDeptIDFromPosition(posObj);
    if (!deptID) {
        return;
    }
    const deptInfo = careersConfig.departments.find((d)=>d.id === deptID);
    if (!deptInfo) {
        Sentry.captureMessage("Failed to find department info for department ID", {
            level: "warning",
            extra: {
                departmentId: deptID,
                positionName: posObj === null || posObj === void 0 ? void 0 : posObj.name,
                positionUid: posObj === null || posObj === void 0 ? void 0 : posObj.uid
            }
        });
        return;
    }
    return deptInfo;
};
const getLocationID = (locationName)=>{
    if (!locationName) return null;
    // NFD decomposition splits characters like "ü" into "u" + combining diacritic, then we strip the diacritics
    // This ensures locations like "Zürich" match the pattern "zurich"
    const normalizedLocation = locationName.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    for (const [locationId, patterns] of Object.entries(LOCATIONS)){
        if (patterns.some((pattern)=>normalizedLocation.includes(pattern))) {
            return locationId;
        }
    }
    Sentry.captureException(new Error("Unrecognized location in position data"), {
        level: "error",
        extra: {
            locationName,
            normalizedLocation,
            availableLocationIds: Object.keys(LOCATIONS)
        }
    });
    return null;
};
//HELPER METHOD
const getLocationInfoFromPosition = (posObj)=>{
    const locID = getLocationIDFromPosition(posObj);
    if (!locID) return;
    const locInfo = careersConfig.locations.find((loc)=>loc.id == locID);
    if (!locInfo) {
        Sentry.captureMessage("Failed to find location info for location ID", {
            level: "warning",
            extra: {
                locationId: locID,
                positionName: posObj === null || posObj === void 0 ? void 0 : posObj.name,
                positionUid: posObj === null || posObj === void 0 ? void 0 : posObj.uid
            }
        });
        return;
    }
    return locInfo;
};
const getLocationIDFromPosition = (posObj)=>{
    const locID = getLocationID(posObj.location.name);
    return locID;
};
const COMEET_POSITION_ID_PATTERN = /^[A-F0-9]{2}\.[A-F0-9]{3}$/;
const isComeetPositionId = (positionId)=>{
    return COMEET_POSITION_ID_PATTERN.test(positionId);
};
const getPositionData = function() {
    var _ref = _async_to_generator(function*(id, positionsType, req) {
        let positionData;
        try {
            if (isComeetPositionId(id)) {
                positionData = yield getPositionDetailsByID(id, positionsType);
            } else {
                positionData = yield getAshbyPositionDetailsByID(id, req);
            }
        } catch (e) {
            if (id === UNLISTED_POSITION_ID) {
                positionData = DEFAULT_UNLISTED_POSITION_DATA;
            } else {
                throw e;
            }
        }
        return positionData;
    });
    return function getPositionData(id, positionsType, req) {
        return _ref.apply(this, arguments);
    };
}();
const UUID_PATTERN = /^[0-9a-f-]{36}$/i;
const isPositionIdValid = (positionId)=>{
    if (!positionId || typeof positionId !== "string") return false;
    return COMEET_POSITION_ID_PATTERN.test(positionId) || UUID_PATTERN.test(positionId);
};
const filterByPositionType = (allPositionsData, positionsType)=>{
    return allPositionsData.filter((position)=>isPositionRelevantToType(position, positionsType));
};
module.exports = {
    UNLISTED_POSITION_ID,
    getDeptID,
    getDeptIDFromPosition,
    getDeptInfo,
    getLocationID,
    getLocationInfoFromPosition,
    getLocationIDFromPosition,
    getPositionData,
    filterByPositionType,
    isPositionIdValid
};
